import { createSelector } from 'reselect';

const getChannelOverview = state => state.channel.channels.channelOverview;
const getChannelResources = state => state.resources.data.channel;
const getChannelId = state => state.channel.channels.selectedChannelId;
const getChannelItemsCount = state => state.channel.channels.channelItemsCount;
const getChannelPushes = state => state.channelPusher.channelPushes;

const getVersion = (channelResources, acesVersionId, piesVersionId) => {
  if (!acesVersionId && !piesVersionId) return undefined;
  const versionId = acesVersionId || piesVersionId;
  const versionResource = acesVersionId
    ? channelResources.aces_versions
    : channelResources.pies_versions;
  return versionResource.find(version => version.id === versionId);
};

const getPlanPushingStatus = (channelId, planId, channelPushes) => {
  const pushedPlan = channelPushes.find(cp => cp.channelId === channelId && cp.planId === planId);

  if (pushedPlan && !pushedPlan.isCompleted) return true;
  return false;
};

const mapChannelPlanResources = (
  channelResources,
  channel,
  channelItemsCount = [],
  channelPushes = []
) => {
  // resource maps for O(1) lookup instead of O(n) find
  const fileTypeMap = (channelResources.file_types || []).reduce((map, ft) => {
    map[ft.id] = ft;
    return map;
  }, {});
  const frequencyMap = (channelResources.delivery_frequencies || []).reduce((map, f) => {
    map[f.id] = f;
    return map;
  }, {});
  const submissionTypeMap = (channelResources.submission_types || []).reduce((map, st) => {
    map[st.id] = st;
    return map;
  }, {});
  const itemCountMap = channelItemsCount.reduce((map, ic) => {
    map[ic.plan_id] = ic.count;
    return map;
  }, {});

  return {
    ...channel,
    plans: channel.plans
      ? channel.plans.map(plan => ({
          ...plan,
          fileType: fileTypeMap[plan.file_type_id],
          frequency: frequencyMap[plan.delivery_frequency_id],
          items_count: itemCountMap[plan.id] || 0,
          version: getVersion(channelResources, plan.aces_version_id, plan.pies_version_id),
          submissionType: submissionTypeMap[plan.submission_type_id],
          pushing: getPlanPushingStatus(channel.id, plan.id, channelPushes),
        }))
      : [],
  };
};

export const getRunningPushIds = createSelector([getChannelPushes], channelPushes =>
  channelPushes.filter(push => !push.isCompleted).map(({ id }) => id)
);

export const mapResourcesToChannelOverview = createSelector(
  [getChannelOverview, getChannelResources, getChannelItemsCount, getChannelPushes],
  (channelOverview, channelResources, channelItemsCount, channelPushes) => {
    const integratedChannels = channelResources ? channelResources.integration_types : [];
    const mappedIntegrated = integratedChannels.map(integratedChannel => {
      // find channel matching integrated channel id
      const customIntegrated = channelOverview.find(
        channel => channel.integration_type_id === integratedChannel.id
      );
      return customIntegrated || { ...integratedChannel, integrated: true };
    });
    // filter channels for integrated channels
    const customChannels = channelOverview.filter(channel => !channel.integration_type_id);

    return [...mappedIntegrated, ...customChannels].map(channel =>
      mapChannelPlanResources(channelResources, channel, channelItemsCount, channelPushes)
    );
  }
);

export const mapSelectedChannelId = createSelector(
  [getChannelId, getChannelOverview, getChannelResources],
  (selectedChannelId, channelOverview, channelResources) => {
    if (!selectedChannelId) return undefined;
    const channel = channelOverview.find(({ id }) => id === selectedChannelId);
    return channel ? mapChannelPlanResources(channelResources, channel) : undefined;
  }
);
